package no.vareflow.app.report

/** Growable byte buffer (common Kotlin, no java.io). */
internal class Bytes(initial: Int = 4096) {
    private var data = ByteArray(initial)
    var size = 0
        private set

    private fun ensure(extra: Int) {
        if (size + extra <= data.size) return
        var capacity = data.size * 2
        while (capacity < size + extra) capacity *= 2
        data = data.copyOf(capacity)
    }

    fun byte(b: Int) { ensure(1); data[size++] = b.toByte() }
    fun bytes(b: ByteArray) { ensure(b.size); b.copyInto(data, size); size += b.size }
    fun ascii(s: String) { ensure(s.length); for (c in s) data[size++] = c.code.toByte() }
    fun utf8(s: String) = bytes(s.encodeToByteArray())
    fun short(v: Int) { byte(v and 0xFF); byte((v ushr 8) and 0xFF) }
    fun int(v: Long) { short((v and 0xFFFF).toInt()); short(((v ushr 16) and 0xFFFF).toInt()) }
    fun int(v: Int) = int(v.toLong() and 0xFFFFFFFFL)
    fun toByteArray(): ByteArray = data.copyOf(size)
}

internal object Crc32 {
    private val table = LongArray(256) { n ->
        var c = n.toLong()
        repeat(8) { c = if (c and 1L != 0L) 0xEDB88320L xor (c ushr 1) else c ushr 1 }
        c
    }

    fun of(bytes: ByteArray): Long {
        var crc = 0xFFFFFFFFL
        for (b in bytes) crc = table[((crc xor b.toLong()) and 0xFF).toInt()] xor (crc ushr 8)
        return crc xor 0xFFFFFFFFL
    }
}

/** Minimal ZIP writer (stored, no compression): all an .xlsx needs. Names are UTF-8. */
internal object ZipStore {
    fun write(entries: List<Pair<String, ByteArray>>): ByteArray {
        val out = Bytes(entries.sumOf { it.second.size + 128 } + 256)
        val central = Bytes()
        // 1980-01-01 00:00 in DOS format.
        val dosTime = 0
        val dosDate = (0 shl 9) or (1 shl 5) or 1
        entries.forEach { (name, content) ->
            val nameBytes = name.encodeToByteArray()
            val crc = Crc32.of(content)
            val offset = out.size.toLong()
            out.int(0x04034b50); out.short(20); out.short(0x0800); out.short(0)
            out.short(dosTime); out.short(dosDate)
            out.int(crc); out.int(content.size.toLong()); out.int(content.size.toLong())
            out.short(nameBytes.size); out.short(0)
            out.bytes(nameBytes); out.bytes(content)

            central.int(0x02014b50); central.short(20); central.short(20); central.short(0x0800); central.short(0)
            central.short(dosTime); central.short(dosDate)
            central.int(crc); central.int(content.size.toLong()); central.int(content.size.toLong())
            central.short(nameBytes.size); central.short(0); central.short(0); central.short(0); central.short(0)
            central.int(0); central.int(offset)
            central.bytes(nameBytes)
        }
        val centralOffset = out.size.toLong()
        val centralBytes = central.toByteArray()
        out.bytes(centralBytes)
        out.int(0x06054b50); out.short(0); out.short(0)
        out.short(entries.size); out.short(entries.size)
        out.int(centralBytes.size.toLong()); out.int(centralOffset); out.short(0)
        return out.toByteArray()
    }
}
